import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2, Store, ArrowRight, Building2 } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { SkillCard } from "@/components/SkillCard";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { KINDS as KIND_CFG, UPLOAD_KINDS } from "@/lib/kinds";

const SORTS = [
  { value: "top_rated", label: "Top rated" },
  { value: "popular", label: "Most popular" },
  { value: "newest", label: "Newest" },
];

// Filter by artifact kind. "all" omits the param (server returns everything).
const KINDS = [{ value: "all", label: "All" }, ...UPLOAD_KINDS.map((k) => ({ value: k, label: KIND_CFG[k].plural }))];

export default function Marketplace() {
  const [skills, setSkills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [kind, setKind] = useState("all");
  const [sort, setSort] = useState("top_rated");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (category && category !== "all") params.set("category", category);
      if (kind && kind !== "all") params.set("kind", kind);
      params.set("sort", sort);
      const { data } = await api.get(`/marketplace?${params.toString()}`);
      setSkills(data);
    } catch (e) {
      toast.error("Failed to load marketplace");
    } finally {
      setLoading(false);
    }
  }, [q, category, kind, sort]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/marketplace/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="rb-display text-2xl font-semibold tracking-tight text-[color:var(--rb-text)]">Marketplace</h1>
        <p className="mt-1 text-sm text-[color:var(--rb-text-faint)]">Discover skills, agents, commands, and more — crafted and uploaded by the community. Free to browse and download.</p>
      </div>

      <Link
        to="/orgs/new"
        data-testid="marketplace-team-cta"
        className="mb-6 flex items-center gap-3 rounded-2xl border border-[color:rgba(212,175,55,0.35)] bg-[color:var(--rb-gold-soft)] px-4 py-3 transition-colors hover:bg-[color:rgba(212,175,55,0.16)]"
      >
        <Building2 className="h-5 w-5 shrink-0 text-[color:var(--rb-gold)]" />
        <div className="flex-1">
          <div className="text-sm font-medium text-[color:var(--rb-gold)]">Use this with your team</div>
          <div className="text-xs text-[color:var(--rb-text-faint)]">A private, governed registry — scan + approval on every artifact, synced to every engineer's machine automatically.</div>
        </div>
        <ArrowRight className="h-4 w-4 shrink-0 text-[color:var(--rb-gold)]" />
      </Link>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--rb-text-faint)]" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search skills…"
            data-testid="marketplace-search-input"
            className="h-11 border-[color:var(--rb-border)] bg-[color:var(--rb-code)] pl-10 text-[color:var(--rb-text)]"
          />
        </div>
        <div className="flex flex-wrap gap-1 rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-code)] p-1" data-testid="marketplace-kind-filter">
          {KINDS.map((k) => (
            <button key={k.value} onClick={() => setKind(k.value)} data-testid={`kind-filter-${k.value}`}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                kind === k.value
                  ? "bg-[color:rgba(45,212,191,0.14)] text-[color:var(--rb-accent)]"
                  : "text-[color:var(--rb-text-dim)] hover:text-[color:var(--rb-text)]"
              }`}>
              {k.label}
            </button>
          ))}
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="marketplace-sort-select" className="h-11 w-full border-[color:var(--rb-border)] bg-[color:var(--rb-code)] text-[color:var(--rb-text)] sm:w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)]">
            {SORTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {categories.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {["all", ...categories].map((c) => (
            <button key={c} onClick={() => setCategory(c)} data-testid="category-chip"
              className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                category === c
                  ? "border-[color:var(--rb-accent)] bg-[color:rgba(45,212,191,0.12)] text-[color:var(--rb-accent)]"
                  : "border-[color:var(--rb-border)] text-[color:var(--rb-text-dim)] hover:text-[color:var(--rb-text)]"
              }`}>
              {c === "all" ? "All" : c}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20 text-[color:var(--rb-text-faint)]"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : skills.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-[color:var(--rb-border)] py-20 text-center">
          <Store className="h-8 w-8 text-[color:var(--rb-text-faint)]" />
          <p className="mt-3 text-sm text-[color:var(--rb-text-faint)]">No skills found. Be the first to publish one!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="marketplace-grid">
          {skills.map((s) => <SkillCard key={s.id} skill={s} />)}
        </div>
      )}
    </AppShell>
  );
}
